import { useEffect, useRef, useState } from "react";

import { DEBOUNCE_WAIT, DYNAMIC_FETCH_THRESHOLD } from "appConfig";

/**
 * @name    useDynamicOptions
 *
 * @summary Manages a local option list that can be dynamically filtered via a
 *          debounced fetch as the user types.  When optionsCount exceeds
 *          DYNAMIC_FETCH_THRESHOLD and a fetchFn is supplied, typing triggers
 *          an API call after DEBOUNCE_WAIT milliseconds and replaces the
 *          visible option list with the results.  On blur the list resets to
 *          the original options.
 *
 *          When fetchFn or optionsCount are omitted (or optionsCount is within
 *          the threshold), fetchOnChange is false and the returned handlers are
 *          no-ops, making the hook safe to use unconditionally.
 *
 * @param   externalOptions  {array|null}   the initial/full option list
 * @param   optionsCount     {number|null}  total records in the database;
 *                                          activates dynamic fetch when
 *                                          optionsCount > DYNAMIC_FETCH_THRESHOLD
 * @param   fetchFn          {function}     async (query: string) => option[]
 *                                          called on debounced input change
 * @returns { options, fetchOnChange, handleInputChange, handleBlur }
 */
export function useDynamicOptions(externalOptions, optionsCount, fetchFn) {
  // null means "no dynamic results" – fall back to externalOptions
  const [dynamicOptions, setDynamicOptions] = useState(null);

  // useRef avoids an extra render cycle just to store the timeout id
  const timeoutRef = useRef(null);

  // tracks fetch generations so a stale in-flight fetch can't overwrite a reset
  const generationRef = useRef(0);

  // when externalOptions change (e.g. after quick-add), clear any dynamic results
  useEffect(() => setDynamicOptions(null), [externalOptions]);

  const options = dynamicOptions ?? externalOptions;

  const fetchOnChange = !!fetchFn && optionsCount > DYNAMIC_FETCH_THRESHOLD;

  function handleInputChange(query, reason) {
    // "reset" fires on selection / clear – immediately revert to externalOptions
    if (reason === "reset") {
      generationRef.current++;
      setDynamicOptions(null);
      return;
    }
    clearTimeout(timeoutRef.current);
    const generation = ++generationRef.current;
    timeoutRef.current = setTimeout(async () => {
      const result = await fetchFn(query);
      // discard if a reset/blur happened while this fetch was in flight
      if (generation === generationRef.current) {
        setDynamicOptions(result);
      }
    }, DEBOUNCE_WAIT);
  }

  function handleBlur() {
    generationRef.current++;
    setDynamicOptions(null);
  }

  return { options, fetchOnChange, handleInputChange, handleBlur };
}
